import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Filter, 
  Download, 
  Search,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Eye,
  BarChart3,
  MapPin,
  Building2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BackButton from './BackButton';
import { LoadingPage, LoadingTableRow } from './LoadingStates';
import { NoTeamMembersEmpty, ErrorState } from './EmptyStates';
import { getManagerTeam, exportCertificationData } from '../services/api';
import { ManagerTeamMember } from '../types';
import { useAuth } from '../context/AuthContext';

const ManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState<ManagerTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    branch: '',
    department: '',
    search: ''
  });
  const [sortConfig, setSortConfig] = useState<{
    key: keyof ManagerTeamMember | 'completion_percentage' | 'expired_certifications' | 'expiring_soon';
    direction: 'asc' | 'desc';
  }>({
    key: 'name',
    direction: 'asc'
  });

  const branches = ['Portland', 'Seattle', 'Anchorage', 'Tri-Cities'];
  const departments = ['Service', 'Testing', 'Construction', 'Electricians'];

  useEffect(() => {
    fetchTeamData();
  }, [filters.branch, filters.department]);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filterParams = {
        ...(filters.branch && { branch: filters.branch }),
        ...(filters.department && { department: filters.department })
      };
      
      const response = await getManagerTeam(filterParams);
      setTeamMembers(response.data.teamMembers || []);
    } catch (err: any) {
      console.error('Error fetching team data:', err);
      setError(err.response?.data?.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: typeof sortConfig.key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const handleExport = async () => {
    try {
      const filterParams = {
        ...(filters.branch && { branch: filters.branch }),
        ...(filters.department && { department: filters.department }),
        ...(filters.search && { search: filters.search })
      };
      
      const response = await exportCertificationData('excel', filterParams);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `team-certifications-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export data');
    }
  };

  const handleViewTechnician = (userId: number) => {
    // Use admin route if user is admin, otherwise use manager route
    const route = user?.is_admin ? `/admin/technician/${userId}` : `/manager/technician/${userId}`;
    navigate(route);
  };

  // Filter and sort team members
  const filteredAndSortedMembers = React.useMemo(() => {
    let filtered = (teamMembers || []).filter(member => {
      const searchLower = filters.search.toLowerCase();
      return (
        member.name.toLowerCase().includes(searchLower) ||
        member.email.toLowerCase().includes(searchLower) ||
        (member.employee_id && member.employee_id.toLowerCase().includes(searchLower))
      );
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = a[sortConfig.key as keyof ManagerTeamMember];
      let bValue: any = b[sortConfig.key as keyof ManagerTeamMember];

      // Handle special sorting cases
      if (sortConfig.key === 'completion_percentage' || 
          sortConfig.key === 'expired_certifications' || 
          sortConfig.key === 'expiring_soon') {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [teamMembers, filters.search, sortConfig]);

  // Calculate summary statistics
  const stats = React.useMemo(() => {
    const total = filteredAndSortedMembers.length;
    const compliant = filteredAndSortedMembers.filter(m => m.completion_percentage >= 100).length;
    const withExpired = filteredAndSortedMembers.filter(m => m.expired_certifications > 0).length;
    const withExpiring = filteredAndSortedMembers.filter(m => m.expiring_soon > 0).length;
    const avgCompletion = total > 0 
      ? filteredAndSortedMembers.reduce((sum, m) => sum + m.completion_percentage, 0) / total 
      : 0;

    return { total, compliant, withExpired, withExpiring, avgCompletion };
  }, [filteredAndSortedMembers]);

  if (loading) {
    return <LoadingPage message="Loading team dashboard..." />;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <BackButton className="mb-6" />
        <ErrorState message={error} onRetry={fetchTeamData} />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <BackButton className="mb-6" />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white">Team Dashboard</h1>
          <p className="text-gray-400 mt-1">Manage your team's certification compliance</p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          <span>Export</span>
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Team</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
            <Users className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Compliant</p>
              <p className="text-2xl font-bold text-green-400">{stats.compliant}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Avg Progress</p>
              <p className="text-2xl font-bold text-white">{stats.avgCompletion.toFixed(0)}%</p>
            </div>
            <BarChart3 className="w-8 h-8 text-purple-400" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Expired</p>
              <p className="text-2xl font-bold text-red-400">{stats.withExpired}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Expiring Soon</p>
              <p className="text-2xl font-bold text-yellow-400">{stats.withExpiring}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 mb-6">
        <div className="flex items-center space-x-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Name, email, or ID..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Branch</label>
              <select
                value={filters.branch}
                onChange={(e) => setFilters(prev => ({ ...prev, branch: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              >
                <option value="">All Branches</option>
                {branches.map(branch => (
                  <option key={branch} value={branch}>{branch}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Department</label>
              <select
                value={filters.department}
                onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setFilters({ branch: '', department: '', search: '' })}
                className="w-full px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Team Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Technician</span>
                    {sortConfig.key === 'name' && (
                      <TrendingUp className={`w-4 h-4 ${sortConfig.direction === 'desc' ? 'transform rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600"
                  onClick={() => handleSort('branch')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Location</span>
                    {sortConfig.key === 'branch' && (
                      <TrendingUp className={`w-4 h-4 ${sortConfig.direction === 'desc' ? 'transform rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Tier
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600"
                  onClick={() => handleSort('completion_percentage')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Progress</span>
                    {sortConfig.key === 'completion_percentage' && (
                      <TrendingUp className={`w-4 h-4 ${sortConfig.direction === 'desc' ? 'transform rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600"
                  onClick={() => handleSort('expired_certifications')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Issues</span>
                    {sortConfig.key === 'expired_certifications' && (
                      <TrendingUp className={`w-4 h-4 ${sortConfig.direction === 'desc' ? 'transform rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <LoadingTableRow key={index} columns={6} />
                ))
              ) : filteredAndSortedMembers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12">
                    <NoTeamMembersEmpty />
                  </td>
                </tr>
              ) : (
                filteredAndSortedMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-white">{member.name}</div>
                        <div className="text-sm text-gray-400">{member.email}</div>
                        {member.employee_id && (
                          <div className="text-xs text-gray-500">ID: {member.employee_id}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-300">
                        {member.branch && (
                          <div className="flex items-center space-x-1">
                            <MapPin className="w-3 h-3" />
                            <span>{member.branch}</span>
                          </div>
                        )}
                      </div>
                      {member.department && (
                        <div className="flex items-center space-x-1 text-xs text-gray-400 mt-1">
                          <Building2 className="w-3 h-3" />
                          <span>{member.department}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {member.tier && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          member.tier.current_tier === 1 ? 'bg-blue-900 text-blue-300' :
                          member.tier.current_tier === 2 ? 'bg-purple-900 text-purple-300' :
                          'bg-yellow-900 text-yellow-300'
                        }`}>
                          Tier {member.tier.current_tier}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-gray-700 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              member.completion_percentage >= 100 ? 'bg-green-600' :
                              member.completion_percentage >= 75 ? 'bg-yellow-600' :
                              member.completion_percentage >= 50 ? 'bg-orange-600' :
                              'bg-red-600'
                            }`}
                            style={{ width: `${Math.min(member.completion_percentage, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm text-white font-medium min-w-[3rem]">
                          {member.completion_percentage.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {member.expired_certifications > 0 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-900 text-red-300">
                            {member.expired_certifications} expired
                          </span>
                        )}
                        {member.expiring_soon > 0 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-900 text-yellow-300">
                            {member.expiring_soon} expiring
                          </span>
                        )}
                        {member.expired_certifications === 0 && member.expiring_soon === 0 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-900 text-green-300">
                            All good
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => handleViewTechnician(member.id)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-300 hover:text-blue-200 hover:bg-blue-900/30 transition-colors"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManagerDashboard;
